/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { VideoProviderConfigurationsService } from "@tivocorp/data-domain-lib";
import type { TVideoProviderConfigurationsGetParams } from "@tivocorp/data-domain-lib";
import type { VideoProviderConfiguration } from "client-code/video-provider-configurations";

import type { IVideoProviderConfigurationInteractor } from "../../interfaces/IVideoProviderConfigurationInteractor";
import type { TNetworkOptions } from "../../types/TNetworkOptions";
import { Log, PostSigninApi, PostSigninLogger } from "../../logging";
import { VideoProviderConfigurationLogger } from "../../logging/VideoProviderConfigurationLogger";
import { DiagnosticSeverity } from "../../types";
import { ApplicationFeatureArea } from "../../enums";
import { TIVO_SERVICES } from "../../../config";
import { BaseInteractor } from "../base";
import { ServiceLocator } from "../base/ServiceLocator";

/**
 * Shared, atomic interactor wrapping {@link VideoProviderConfigurationsService}.
 *
 * Composed by feature interactors that need provider configuration data
 * (Playback/DRM, session management, …). The
 * {@link ApplicationFeatureArea} is fixed at construction time.
 *
 * Stateless: a fresh `VideoProviderConfigurationsService` is built per call
 * via {@link ServiceLocator}. Response caching lives in the service layer.
 */
export class VideoProviderConfigurationInteractor
  extends BaseInteractor
  implements IVideoProviderConfigurationInteractor
{
  private static readonly TAG: string = "VideoProviderConfigurationInteractor";

  public constructor(
    private readonly mFeatureArea: ApplicationFeatureArea = ApplicationFeatureArea.ALL
  ) {
    super();
  }

  private async createService(): Promise<VideoProviderConfigurationsService> {
    return ServiceLocator.getInstance(
      VideoProviderConfigurationsService,
      await this.buildServicesParams(
        TIVO_SERVICES.VideoProviderConfigurationsService,
        this.mFeatureArea
      )
    );
  }

  public async fetchVideoProviderConfigurations(
    params: TVideoProviderConfigurationsGetParams,
    options?: TNetworkOptions
  ): Promise<Array<VideoProviderConfiguration>> {
    return this.executeGuarded(
      "fetchVideoProviderConfigurations",
      async (
        signal: AbortSignal
      ): Promise<Array<VideoProviderConfiguration>> => {
        Log.d(
          VideoProviderConfigurationInteractor.TAG,
          "fetchVideoProviderConfigurations() called"
        );
        const service: VideoProviderConfigurationsService =
          await this.createService();
        const startTimeMs: number = Date.now();
        try {
          const response = (await service.fetchVideoProviderConfigurationsV2(
            params,
            signal
          )) as Array<VideoProviderConfiguration>;
          PostSigninLogger.logApiSuccess(
            PostSigninApi.VideoProviderConfiguration,
            Date.now() - startTimeMs,
            {
              provider_count: String(
                Array.isArray(response) ? response.length : 0
              )
            }
          );
          return response;
        } catch (error: unknown) {
          PostSigninLogger.logApiFailure(
            PostSigninApi.VideoProviderConfiguration,
            error,
            DiagnosticSeverity.Error,
            Date.now() - startTimeMs,
            VideoProviderConfigurationLogger.getFailureFields(error)
          );
          throw error;
        }
      },
      options
    );
  }

  public async fetchVPConfigurationsForVideoPlayer(
    providerPartnerId: string,
    forceNetworkCall?: boolean,
    options?: TNetworkOptions
  ): Promise<Array<VideoProviderConfiguration>> {
    return this.executeGuarded(
      "fetchVPConfigurationsForVideoPlayer",
      async (): Promise<Array<VideoProviderConfiguration>> => {
        Log.d(
          VideoProviderConfigurationInteractor.TAG,
          "fetchVPConfigurationsForVideoPlayer() called"
        );
        const service: VideoProviderConfigurationsService =
          await this.createService();
        return (await service.fetchVPConfigurationsForVideoPlayer(
          providerPartnerId,
          forceNetworkCall
        )) as Array<VideoProviderConfiguration>;
      },
      options
    );
  }

  public async clearCache(): Promise<void> {
    Log.d(VideoProviderConfigurationInteractor.TAG, "clearCache() called");
    const service: VideoProviderConfigurationsService =
      await this.createService();
    await service.clearCache();
  }
}
